import React, { AriaAttributes } from 'react';

import uniqueId from 'lodash/uniqueId';

import { useClassnames, visuallyHidden } from '@lumapps/classnames';
import { Text } from '@lumapps/lumx/react';

import { BASE_CLASSNAME } from '../constants';
import { MenuContext } from '../context';

import './index.scss';

export interface MenuSectionProps {
    /** Forwarded class name */
    className?: string;
    /** Section content (menu items, sections, submenus, etc.) */
    children?: React.ReactNode;
    /** Section label */
    label?: React.ReactNode;
    /** Hide section */
    ['aria-hidden']?: AriaAttributes['aria-hidden'];
}

export const CLASSNAME = `${BASE_CLASSNAME}-section`;

/**
 * Menu section (ARIA `group`).
 *
 * To use inside a `Menu` or `Menubar`.
 *
 * @family Menus
 */
export const MenuSection = React.forwardRef((props: MenuSectionProps, ref: React.Ref<HTMLElement>) => {
    const { className, children, label, 'aria-hidden': ariaHidden, ...forwardedProps } = props;
    const { block, element } = useClassnames(CLASSNAME);
    const [section, sectionRef] = React.useState<HTMLUListElement | null>(null);
    const isHidden = ariaHidden === 'true' || ariaHidden === true || section?.matches(':empty');
    const sectionId = !isHidden ? uniqueId(CLASSNAME) : undefined;
    const context = React.useContext(MenuContext);

    // Don't render the section if it has no items
    const childrenArray = React.Children.toArray(children);
    if (!childrenArray.length || childrenArray.every((child) => !child)) {
        return null;
    }

    return (
        <li
            ref={ref as React.Ref<HTMLLIElement>}
            role={!isHidden ? 'none' : undefined}
            aria-hidden={ariaHidden}
            className={block(
                {
                    [`theme-${context?.menuVariant?.theme}`]: !!context?.menuVariant?.theme,
                    [`orientation-${context?.orientation}`]: !!context?.orientation,
                },
                [className, isHidden && visuallyHidden()],
            )}
            {...forwardedProps}
        >
            {label && (
                <Text
                    as="span"
                    className={element('header', undefined, 'lumx-list-subheader')}
                    aria-hidden="true"
                    id={sectionId}
                >
                    {label}
                </Text>
            )}
            <ul
                ref={sectionRef}
                role={!isHidden ? 'group' : undefined}
                aria-labelledby={sectionId}
                className={element('content')}
            >
                {children}
            </ul>
        </li>
    );
});

MenuSection.displayName = 'MenuSection';
